import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { onlyText } from 'react-children-utilities';
import { theme } from '@frontend/theme';
import { Text, useTooltip } from '@frontend/design-system';

export type CustomYAxisTickProps = {
  label?: ReactNode;
  onChartSizeChange?: (isLargeChart: boolean) => void;
  useCustomTransitionsInSmallMode?: boolean;
  value?: number | string;
  x?: number;
  xTransitionLabel?: number;
  xTransitionValue?: number;
  y?: number;
};

const smallScreenDefaultClipLength = 10;
const largeScreenDefaultClipLength = 25;
const defaultValueXTransition = 0;
const defaultXTransitionWithoutValue = -90;
const defaultXTransitionWithValue = -130;

export const YAxisLabelValueTick = ({
  label = '',
  onChartSizeChange,
  useCustomTransitionsInSmallMode,
  value,
  x,
  xTransitionLabel,
  xTransitionValue = defaultValueXTransition,
  y,
}: CustomYAxisTickProps) => {
  const [isLargeChart, setIsLargeChart] = useState<boolean>(false);
  const { Tooltip: Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });
  const groupRef = useRef<SVGGElement | null>(null);

  useLayoutEffect(() => {
    if (!groupRef.current) {
      return;
    }

    const chartElement = groupRef.current.closest('.chart-card');
    if (!chartElement) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsLargeChart(entry.target.clientWidth >= 600);
      }
    });

    observer.observe(chartElement);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (onChartSizeChange) {
      onChartSizeChange(isLargeChart);
    }
  }, [isLargeChart, onChartSizeChange]);

  if (!x || !y) {
    return null;
  }

  const finalClipLength = isLargeChart ? largeScreenDefaultClipLength : smallScreenDefaultClipLength;
  const labelOnlyText = onlyText(label);
  const labelMod =
    labelOnlyText.length > finalClipLength ? `${labelOnlyText.slice(0, finalClipLength)}...` : labelOnlyText;

  const defaultXTransition =
    typeof value === 'undefined' ? defaultXTransitionWithoutValue : defaultXTransitionWithValue;
  const finalXTransitionLabel =
    isLargeChart || useCustomTransitionsInSmallMode ? xTransitionLabel ?? defaultXTransition - 50 : defaultXTransition;

  return (
    <g
      {...(labelOnlyText !== labelMod ? triggerProps : {})}
      ref={(node) => {
        groupRef.current = node;
        triggerProps.ref(node);
      }}
    >
      <text
        fill={theme.colors.neutral50}
        style={{
          fontSize: theme.fontSize(14),
          fontWeight: 'bold',
          textAnchor: 'start',
        }}
        transform={`translate(${x + finalXTransitionLabel}, ${y + 4})`}
      >
        <tspan>{labelMod}</tspan>
      </text>

      {typeof value !== 'undefined' && (
        <text
          style={{
            fontSize: theme.fontSize(14),
            textAnchor: 'end',
            fontWeight: 'bold',
          }}
          transform={`translate(${x + xTransitionValue}, ${y + 4})`}
        >
          <tspan fill={theme.colors.neutral80}>{value}</tspan>
        </text>
      )}

      {label !== labelMod && (
        <Tooltip {...tooltipProps}>
          <Text size='medium' color='white'>
            {label}
          </Text>
        </Tooltip>
      )}
    </g>
  );
};
